import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useEmployee, TestAssignment } from '@/contexts/EmployeeContext'
import { useToast } from '@/hooks/use-toast'
import { BookOpen, ClipboardList, LogOut, CheckCircle, Clock, User, KeyRound } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import TestAccessCodeForm from '@/components/TestAccessCodeForm'

const EmployeeDashboard = () => {
  const navigate = useNavigate()
  const { getEmployeeById, getEmployeeTests } = useEmployee()
  const { toast } = useToast()

  const [employee, setEmployee] = useState<any>(null)
  const [assignments, setAssignments] = useState<TestAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [showAccessForm, setShowAccessForm] = useState(false)

  useEffect(() => {
    const employeeId = sessionStorage.getItem('currentEmployeeId')

    if (!employeeId) {
      toast({
        title: 'Oturum Bulunamadı',
        description: 'Lütfen tekrar giriş yapın.',
        variant: 'destructive',
      })
      navigate('/employee/login')
      return
    }

    const employeeData = getEmployeeById(employeeId)
    if (!employeeData) {
      toast({
        title: 'Çalışan Bulunamadı',
        description: 'Hesabınız silinmiş olabilir. Lütfen yöneticinize başvurun.',
        variant: 'destructive',
      })
      sessionStorage.removeItem('currentEmployeeId')
      sessionStorage.removeItem('currentEmployeeName')
      navigate('/employee/login')
      return
    }

    setEmployee(employeeData)
    setAssignments(getEmployeeTests(employeeId))
    setLoading(false)
  }, [getEmployeeById, getEmployeeTests, navigate, toast])

  const handleLogout = () => {
    sessionStorage.removeItem('currentEmployeeId')
    sessionStorage.removeItem('currentEmployeeName')
    navigate('/employee/login')
    toast({
      title: 'Çıkış Yapıldı',
      description: 'Başarıyla çıkış yaptınız.',
    })
  }

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('tr-TR')
  }

  const getDueStatus = (dueDate: string, status: string) => {
    if (status === 'completed') {
      return {
        icon: <CheckCircle className="h-5 w-5 text-green-500" />,
        text: 'Tamamlandı',
        color: 'text-green-600',
      }
    }

    const today = new Date()
    const due = new Date(dueDate)
    const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

    if (daysLeft < 0) {
      return {
        icon: <Clock className="h-5 w-5 text-red-500" />,
        text: 'Süresi Doldu',
        color: 'text-red-600',
      }
    } else if (daysLeft === 0) {
      return {
        icon: <Clock className="h-5 w-5 text-yellow-500" />,
        text: 'Bugün Son Gün',
        color: 'text-yellow-600',
      }
    } else {
      return {
        icon: <Clock className="h-5 w-5 text-blue-500" />,
        text: `${daysLeft} Gün Kaldı`,
        color: 'text-blue-600',
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p>Yükleniyor...</p>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 rounded-full p-2">
              <User className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{employee?.name}</h1>
              <p className="text-gray-500">
                {employee?.department} - {employee?.role}
              </p>
            </div>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button variant="secondary" onClick={() => setShowAccessForm(!showAccessForm)}>
              <KeyRound className="mr-2 h-4 w-4" />
              {showAccessForm ? 'Sınavları Göster' : 'Giriş Kodu ile Başla'}
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Çıkış Yap
            </Button>
          </div>
        </div>

        {showAccessForm ? (
          <div className="mt-8">
            <TestAccessCodeForm />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Atanan Sınavlar</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{assignments.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Tamamlanan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {assignments.filter((a) => a.status === 'completed').length}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Bekleyen</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-600">
                    {assignments.filter((a) => a.status === 'pending').length}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Atanan Sınavlar</CardTitle>
                <CardDescription>
                  Tamamlamanız gereken sınavlar aşağıda listelenmiştir
                </CardDescription>
              </CardHeader>
              <CardContent>
                {assignments.length > 0 ? (
                  <div className="space-y-4">
                    {assignments.map((assignment) => {
                      const status = getDueStatus(assignment.dueDate, assignment.status)

                      return (
                        <Card key={assignment.id}>
                          <CardContent className="p-0">
                            <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="md:col-span-2">
                                <div className="flex items-start gap-3">
                                  <div className="bg-gray-100 rounded p-2 mt-1">
                                    <BookOpen className="h-5 w-5 text-gray-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-medium text-lg">{assignment.testName}</h3>
                                    <p className="text-sm text-gray-500">
                                      Atanma Tarihi: {formatDate(assignment.assignedAt)}
                                    </p>

                                    <div className="flex items-center gap-2 mt-2">
                                      {status.icon}
                                      <span className={`text-sm font-medium ${status.color}`}>
                                        {status.text}
                                      </span>
                                    </div>

                                    {assignment.accessCode && (
                                      <div className="mt-3 p-2 bg-gray-50 rounded border inline-flex items-center">
                                        <KeyRound className="h-4 w-4 text-gray-500 mr-2" />
                                        <span className="font-mono font-medium">
                                          {assignment.accessCode}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-end">
                                {assignment.status === 'completed' ? (
                                  <Button variant="outline" disabled>
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Tamamlandı
                                  </Button>
                                ) : (
                                  <Button asChild>
                                    <Link to={`/interview?assignmentId=${assignment.id}`}>
                                      <ClipboardList className="mr-2 h-4 w-4" />
                                      Sınava Başla
                                    </Link>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      Henüz size atanmış bir sınav bulunmamaktadır.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <Footer />
    </div>
  )
}

export default EmployeeDashboard
